import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DiscussionNode } from '@proref/types';
import {
  deleteComment,
  editComment,
  listDiscussions,
  postComment,
  unvoteComment,
  voteComment,
} from '../services/discussions.service';

// Apply an update to one comment (root OR reply) in the cached 2-level tree, leaving the rest
// untouched. Used by the optimistic vote path to patch a single node without refetching.
function updateNode(
  nodes: DiscussionNode[],
  id: string,
  fn: (n: DiscussionNode) => DiscussionNode,
): DiscussionNode[] {
  return nodes.map((n) =>
    n.id === id ? fn(n) : n.replies.length ? { ...n, replies: updateNode(n.replies, id, fn) } : n,
  );
}

// A clip's thread. Enable only once the referee has answered (viewer.has_answered) — the API
// gates it, so requesting it early would 403. Fetch-based; live updates land with M8 (Realtime).
export function useDiscussionsQuery(clipId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['discussions', clipId],
    queryFn: () => listDiscussions(clipId as string),
    enabled: Boolean(clipId) && enabled,
    // Threads change with user actions (post/vote), which invalidate this key; 30s keeps a
    // reopen of the same clip instant. Live updates land with M8 (Realtime).
    staleTime: 30_000,
  });
}

// Every mutation invalidates the clip's thread so the list reflects the change. No realtime yet,
// so a refetch is how other users' comments show up too (until M8 wires the live subscription).
export function usePostComment(clipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; parent_id?: string }) => postComment(clipId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['discussions', clipId] }),
  });
}

export function useEditComment(clipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => editComment(id, { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['discussions', clipId] }),
  });
}

export function useDeleteComment(clipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['discussions', clipId] }),
  });
}

// Upvote toggle — OPTIMISTIC. The ▲ count + pressed state flip the instant it's clicked; we
// patch only the one comment in the cached tree (no full-thread refetch, which used to flicker
// + reorder the whole list on every vote). onSuccess reconciles that node to the server's exact
// count; onError rolls the whole tree back to its pre-click snapshot. No success toast (the count
// change is the feedback) — errors are surfaced by the caller.
export function useVoteComment(clipId: string) {
  const qc = useQueryClient();
  const key = ['discussions', clipId] as const;

  return useMutation({
    mutationFn: ({ id, add }: { id: string; add: boolean }) => (add ? voteComment(id) : unvoteComment(id)),
    onMutate: async ({ id, add }: { id: string; add: boolean }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<DiscussionNode[]>(key);
      if (prev) {
        qc.setQueryData<DiscussionNode[]>(
          key,
          updateNode(prev, id, (n) => ({
            ...n,
            has_voted: add,
            upvotes: Math.max(0, n.upvotes + (add ? 1 : -1)),
          })),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSuccess: (result, { id }) => {
      // Reconcile just this node to the server's authoritative count — no whole-thread refetch.
      const current = qc.getQueryData<DiscussionNode[]>(key);
      if (current) {
        qc.setQueryData<DiscussionNode[]>(
          key,
          updateNode(current, id, (n) => ({ ...n, upvotes: result.upvotes, has_voted: result.has_voted })),
        );
      }
    },
  });
}
